import {
  FormEvent,
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from "react";
import axios, { CancelTokenSource } from "axios";

import "./FilterAssist.css";

import { useAuth } from "../../utility/useAuth";
import { retireSmartSearch, useSmartSearchUsable } from "../../utility/searchMode";
import { askBoardFilter, BoardFilterResult } from "../../services/ApiService";
import { BoardFilterOptions } from "../../utility/filterIntent";
import { logger } from "../../utility/logger";
import SEARCH_SRC from "../../Icons/Search.svg";

/** A filter proposal that actually carries something to apply. */
export type AppliableFilter = Extract<BoardFilterResult, { kind: "filter" }>;

/** What applying a proposal hands back: the line to show, and the filters it describes. */
export interface AssistApplied {
  /** The line to show underneath — or `""` for nothing. */
  message: string;
  /** `filterSignature` as it will be once the applied filters land. */
  signature: string;
}

interface FilterAssistProps {
  /** What the pickers are offering right now. Sent with the request AND re-checked against. */
  options: BoardFilterOptions;

  /**
   * Apply the proposal and return the line to show underneath, with the signature of
   * the filters that line describes.
   *
   * ⚠️ THE APPLYING IS THE PARENT'S, DELIBERATELY. `GlobalFilter` owns the filters
   * (that ownership is itself a bug fix — see the "week filter comes back checked but
   * unfiltered" entry in AGENTS.md, where two components wrote one store field and
   * raced on mount). This component owns a text box and a request, and nothing else.
   */
  onApply: (result: AppliableFilter) => AssistApplied;

  /**
   * PLAIN SEARCH FIRST. Called with the text before any model call: if a plain search would
   * find something on the board (an order number, a stock code, a customer), the parent runs
   * that search and returns `true` — and CoSMo is never asked. The term then stays in this
   * box (it comes back as `activeSearchText`). `false` means nothing matched, so the text
   * goes on to the smart filter. Optional so the box still works mounted on its own.
   */
  tryBasicSearch?: (text: string) => boolean;

  /**
   * The free-text search currently applied to the board ("" for none) — from a plain search
   * run here, a barcode scan, or a session restored from storage. The box shows it, as the
   * plain box always has, so an active search is never invisible.
   */
  activeSearchText?: string;

  /** Clear that search — the box was emptied, or a CoSMo question is replacing it. */
  onClearSearch?: () => void;

  /**
   * The bar's current filters, as one comparable string. The "Showing …" line is only
   * shown while this still equals the signature it was written for: once a chip is
   * removed, Clear filters is pressed or a picker adds one, the line describes chips
   * that are no longer there and is hidden.
   */
  filterSignature: string;
}

/**
 * "Describe what you want to see" for the job board.
 *
 * ⚠️ IT IS NOT THE SEARCH BOX, AND MUST NOT BECOME IT. `GlobalSearch`'s box does a
 * plain contains-match on production order numbers and card text, instantly and for
 * free; sending every order number somebody types to a model would be slower, dearer
 * and worse at the one thing that box is for. This is the other question — "late
 * orders for Acme in 01" — and it answers by SETTING THE PICKERS, so what happens is
 * visible as chips the person can read, change and clear.
 *
 * Where CoSMo can run, this IS the board's search field (2026-10-01): text a plain
 * search would find is searched plainly first (`tryBasicSearch`), and only text that
 * matches nothing is sent — see `utility/searchMode.ts`. The plain box takes over
 * where CoSMo cannot run.
 *
 * Three things it deliberately does:
 *
 * - **It disappears rather than erroring when the feature is not here.** Absent
 *   `supportAssistantEnabled` means the whole feature does not exist (the flag's
 *   documented polarity), and a `503`/`404` from the Function App means the same
 *   thing arriving later — an environment with no model deployment, or one a release
 *   ahead. Both retire the box for the session, exactly as the chat panel does,
 *   because a control that can only fail is worse than no control.
 * - **It says what it did.** The line underneath names every filter that was applied
 *   and everything it could not match. A filter that quietly did four of the five
 *   things asked of it looks exactly like one that did all five.
 * - **It cancels in flight.** The request outlives the bar — which unmounts whenever
 *   the panel is collapsed or the user leaves `/home` — so the token is a ref and the
 *   cleanup cancels it.
 */
const FilterAssist: FunctionComponent<FilterAssistProps> = ({
  options,
  onApply,
  tryBasicSearch,
  activeSearchText = "",
  onClearSearch,
  filterSignature,
}) => {
  const auth = useAuth();

  const [draft, setDraft] = useState(activeSearchText);

  // Follow the store, not the keystrokes: it only changes when a search is committed
  // (here, by a scan, by Clear filters), so it never fights somebody mid-type.
  useEffect(() => {
    setDraft(activeSearchText);
  }, [activeSearchText]);
  const [asking, setAsking] = useState(false);
  const [message, setMessage] = useState("");
  // The filters `message` describes, or null for a line that is not about the
  // filters (a refusal, "busy"), which stays until the next ask or keystroke.
  const [messageFilters, setMessageFilters] = useState<string | null>(null);

  /**
   * Set once the BFF says this environment has no assistant, and never unset.
   *
   * A local flag rather than a module-level one on purpose: the bar unmounts often
   * (every collapse of the filter panel), so a session-wide memo would be the more
   * efficient choice — but it would also survive a region hop, and a CORP user
   * switching into an org whose Function App IS configured would keep the box hidden
   * with nothing to explain it.
   */
  const [unavailable, setUnavailable] = useState(false);

  const pending = useRef<CancelTokenSource | null>(null);

  useEffect(
    () => () => {
      pending.current?.cancel();
      pending.current = null;
    },
    []
  );

  // The flag, and any earlier retirement this mount of the board — the box can be remounted by
  // moving between the search row and the bar, and must not come back from a 503 by doing so.
  const usable = useSmartSearchUsable();

  if (!usable || unavailable) return null;

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();

    const question = draft.trim();
    if (!question || asking) return;

    // Instant, free and exact when it applies — see `tryBasicSearch`.
    if (tryBasicSearch?.(question)) {
      setMessage("");
      setMessageFilters(null);
      return;
    }

    pending.current?.cancel();
    const source = axios.CancelToken.source();
    pending.current = source;

    setAsking(true);
    setMessage("");
    setMessageFilters(null);

    try {
      const result = await askBoardFilter(auth, question, options, source.token);

      switch (result.kind) {
        case "filter":
          // The parent applies it and hands back the sentence, so the words under the
          // box describe what the CHIPS ended up being — not what was asked for.
          {
            // The answer replaces a plain search that is still applied — otherwise CoSMo's
            // chips would sit on top of a text filter the box stops showing below. Cleared
            // HERE rather than before asking, so the question stays in the box meanwhile.
            if (activeSearchText) onClearSearch?.();
            const applied = onApply(result);
            setMessage(applied.message);
            setMessageFilters(applied.signature);
          }
          setDraft("");
          break;

        case "refused":
          setMessage(
            result.message ??
              "I couldn't turn that into filters. Try naming a warehouse, a resource or a customer."
          );
          break;

        case "busy":
          setMessage("Busy at the moment — try that again in a minute.");
          break;

        case "unavailable":
          setUnavailable(true);
          // Tells the search row to fall back to the plain box.
          retireSmartSearch();
          break;

        default:
          setMessage("I couldn't do that just now. The filters above still work.");
          break;
      }
    } catch (error) {
      // The only throw `askBoardFilter` has is the cancellation, and a cancelled
      // request is not a failure — the bar unmounted, or the user asked again.
      logger.log("The board filter request was cancelled.", error);
      return;
    } finally {
      if (pending.current === source) {
        pending.current = null;
        setAsking(false);
      }
    }
  };

  // Derived, not cleared by an effect: the setters land in the store a render apart
  // from this component's own state, and comparing on every render cannot race that.
  const note =
    messageFilters === null || messageFilters === filterSignature ? message : "";

  return (
    <form className={asking ? "fa-wrap fa-wrap--asking" : "fa-wrap"} onSubmit={submit} data-csm-step="filter.assist">
      <div className="fa-box">
        <button
          type="submit"
          className="fa-go"
          aria-label="Set the filters from this description"
          title="Search, or set the filters from this description"
          disabled={asking || !draft.trim()}
        >
          <img src={SEARCH_SRC} alt="" aria-hidden="true" className="fa-go-icon" />
        </button>

        <input
          className="fa-input"
          type="text"
          value={draft}
          disabled={asking}
          onChange={(e) => {
            setDraft(e.target.value);
            // Emptying the box clears an applied search, as the plain box does.
            if (e.target.value === "" && activeSearchText) onClearSearch?.();
            // The old line described the chips that are still on screen, so it stays
            // until the next answer replaces it — but it must not sit under a box the
            // person has started retyping, where it reads as a reply to that.
            if (message) setMessage("");
          }}
          placeholder="Search all production orders or describe what to show"
          aria-label="Search all production orders, or describe what to show and the filters will be set for you"
        />
      </div>

      {(asking || note) && (
        <div className="fa-note" role="status">
          {asking ? "CoSMo is working this out…" : note}
        </div>
      )}
    </form>
  );
};

export default FilterAssist;
